/* eslint-disable max-lines */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  RefreshCw,
  AlertTriangle,
  Skull,
  Clock,
  Copy,
  Check,
  Eye,
  RotateCcw,
  Play,
  XCircle,
  Trash2
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useGetOperationsLive } from '@utils/api/back-office/operations/use-get-operations-live';
import {
  useRequeueSession,
  useRestartSession,
  useKillSession,
  useCleanupSession,
  useBulkSessionAction
} from '@utils/api/back-office/operations/use-operations-actions';
import {
  OperationsSession,
  SESSION_STATUS_OPTIONS,
  SessionStatus
} from '@utils/types/back-office/operations';
import toast from 'react-hot-toast';

const STATUS_CHIP_ACTIVE: Record<string, string> = {
  queued: 'bg-blue-500 text-white border-transparent',
  processing: 'bg-amber-500 text-white border-transparent',
  streaming: 'bg-sky-500 text-white border-transparent',
  finished: 'bg-emerald-500 text-white border-transparent',
  error: 'bg-red-500 text-white border-transparent',
  failed: 'bg-red-500 text-white border-transparent',
  stranded: 'bg-orange-500 text-white border-transparent'
};

type StatusFilter = SessionStatus | 'all';

type BulkAction = 'requeue' | 'restart' | 'kill' | 'cleanup';

/**
 * On-Prem Admin Operations Page
 * Monitor sessions, queue status, and perform operational tasks.
 */
export default function AdminOperations() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (searchParams.get('status') as StatusFilter) || 'all';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [bulkActionModal, setBulkActionModal] = useState<{
    open: boolean;
    action: BulkAction | null;
  }>({ open: false, action: null });
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    sessionId: string;
    action: BulkAction | null;
  }>({ open: false, sessionId: '', action: null });

  // Action mutations
  const requeueMutation = useRequeueSession();
  const restartMutation = useRestartSession();
  const killMutation = useKillSession();
  const cleanupMutation = useCleanupSession();
  const bulkActionMutation = useBulkSessionAction();

  const limit = parseInt(searchParams.get('limit') || '25', 10);
  const offset = parseInt(searchParams.get('offset') || '0', 10);

  const { data, isFetching, refetch, dataUpdatedAt, isError } =
    useGetOperationsLive({
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit,
      offset,
      time_range: 'all'
    });

  const sessions = data?.result ?? [];
  const totalResults = data?.total_results ?? 0;
  const sessionCounts = data?.session_counts;
  const systemHealth = data?.system_health;

  // Format last updated time
  const lastUpdated = useMemo(() => {
    if (!dataUpdatedAt) return '';
    return new Date(dataUpdatedAt).toLocaleTimeString();
  }, [dataUpdatedAt]);

  // Check if there are any alerts to show
  const alerts = useMemo(() => {
    if (!systemHealth) return [];
    const alertList: {
      key: string;
      label: string;
      value: number;
      icon: typeof AlertTriangle;
      bgClass: string;
      iconClass: string;
      textClass: string;
    }[] = [];

    if (systemHealth.stranded_count > 0) {
      alertList.push({
        key: 'stranded',
        label: t('stranded-sessions'),
        value: systemHealth.stranded_count,
        icon: AlertTriangle,
        bgClass: 'bg-orange-500/10',
        iconClass: 'text-orange-500',
        textClass: 'text-orange-600 dark:text-orange-400'
      });
    }
    if (systemHealth.stuck_processing_count > 0) {
      alertList.push({
        key: 'stuck',
        label: t('stuck-processing'),
        value: systemHealth.stuck_processing_count,
        icon: Clock,
        bgClass: 'bg-amber-400/10',
        iconClass: 'text-amber-500',
        textClass: 'text-amber-600 dark:text-amber-400'
      });
    }
    if (systemHealth.queue_dead_letter > 0) {
      alertList.push({
        key: 'dead-letter',
        label: t('dead-letter-queue'),
        value: systemHealth.queue_dead_letter,
        icon: Skull,
        bgClass: 'bg-red-500/10',
        iconClass: 'text-red-500',
        textClass: 'text-red-600 dark:text-red-400'
      });
    }
    if (systemHealth.error_count_24h > 0) {
      alertList.push({
        key: 'errors',
        label: t('errors-24h'),
        value: systemHealth.error_count_24h,
        icon: AlertTriangle,
        bgClass: 'bg-red-500/10',
        iconClass: 'text-red-500',
        textClass: 'text-red-600 dark:text-red-400'
      });
    }
    return alertList;
  }, [systemHealth, t]);

  // Get session count for a status
  const getStatusCount = (status: string): number => {
    if (!sessionCounts) return 0;
    const backendCount = (sessionCounts as Record<string, number>)[status] ?? 0;
    if (backendCount === 0) {
      return sessions.filter(s => s.status === status).length;
    }
    return backendCount;
  };

  const handleStatusChange = (status: StatusFilter) => {
    setStatusFilter(status);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('offset');
    if (status === 'all') {
      newParams.delete('status');
    } else {
      newParams.set('status', status);
    }
    setSearchParams(newParams);
  };

  const handleCopyId = useCallback(
    (sessionId: string) => {
      navigator.clipboard.writeText(sessionId);
      setCopiedId(sessionId);
      toast.success(t('copied-to-clipboard'));
      setTimeout(() => setCopiedId(null), 2000);
    },
    [t]
  );

  // Session action handlers
  const handleSessionAction = useCallback(
    (sessionId: string, action: BulkAction) => {
      setConfirmModal({ open: true, sessionId, action });
    },
    []
  );

  const executeSessionAction = () => {
    const { sessionId, action } = confirmModal;
    if (!sessionId || !action) return;

    const actionMap = {
      requeue: requeueMutation,
      restart: restartMutation,
      kill: killMutation,
      cleanup: cleanupMutation
    };

    const mutation = actionMap[action];
    mutation.mutate(sessionId, {
      onSuccess: () => {
        toast.success(t(`session-${action}-success`));
        setConfirmModal({ open: false, sessionId: '', action: null });
      },
      onError: () => {
        toast.error(t(`session-${action}-failed`));
      }
    });
  };

  // Bulk action handlers
  const handleBulkAction = (action: BulkAction) => {
    if (selectedSessions.length === 0) {
      toast.error(t('no-sessions-selected'));
      return;
    }
    setBulkActionModal({ open: true, action });
  };

  const executeBulkAction = () => {
    const { action } = bulkActionModal;
    if (!action || selectedSessions.length === 0) return;

    bulkActionMutation.mutate(
      { session_ids: selectedSessions, action },
      {
        onSuccess: data => {
          toast.success(
            t('bulk-action-success', {
              count: data.affected_count,
              total: selectedSessions.length
            })
          );
          setSelectedSessions([]);
          setBulkActionModal({ open: false, action: null });
        },
        onError: () => {
          toast.error(t('bulk-action-failed'));
        }
      }
    );
  };

  const getActionLabel = useCallback(
    (action: BulkAction | null): string => {
      if (!action) return '';
      const labels: Record<BulkAction, string> = {
        requeue: t('requeue'),
        restart: t('restart'),
        kill: t('kill'),
        cleanup: t('cleanup')
      };
      return labels[action];
    },
    [t]
  );

  const isActionLoading =
    requeueMutation.isLoading ||
    restartMutation.isLoading ||
    killMutation.isLoading ||
    cleanupMutation.isLoading ||
    bulkActionMutation.isLoading;

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('just-now');
    if (diffMins < 60) return `${diffMins}m ${t('ago')}`;
    if (diffHours < 24) return `${diffHours}h ${t('ago')}`;
    return `${diffDays}d ${t('ago')}`;
  };

  // Status chip component
  const StatusChip = ({
    status,
    label,
    count,
    active
  }: {
    status: StatusFilter;
    label: string;
    count?: number;
    active: boolean;
  }) => {
    const activeClass = status !== 'all' ? STATUS_CHIP_ACTIVE[status] : null;

    return (
      <button
        onClick={() => handleStatusChange(status)}
        // eslint-disable-next-line no-restricted-syntax
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
          active
            ? (activeClass ?? 'border-transparent text-white')
            : 'border-hairline'
        }`}
        style={
          active && !activeClass
            ? { backgroundColor: getColour('activeText') }
            : active
              ? undefined
              : {
                  backgroundColor: getColour('secondLayer'),
                  color: getColour('normalText')
                }
        }
      >
        {label}
        {count !== undefined && (
          <span
            // eslint-disable-next-line no-restricted-syntax
            className={`px-1.5 py-0.5 rounded-full text-xs ${active ? 'bg-white/20' : ''}`}
            style={
              active ? undefined : { backgroundColor: getColour('thirdLayer') }
            }
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  type Row = OperationsSession;

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'status',
        header: t('status'),
        field: 'status',
        accessorKey: 'status',
        minWidth: 100,
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-1">
            <StatusBadge
              status={getValue() as string}
              label={t(getValue() as string)}
            />
            {row.original.is_stuck && (
              <span title={t('stuck')}>
                <Clock className="h-3.5 w-3.5 text-orange-500 dark:text-orange-400" />
              </span>
            )}
            {row.original.is_stranded && (
              <span title={t('stranded')}>
                <AlertTriangle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
              </span>
            )}
          </div>
        )
      },
      {
        id: 'session_id',
        header: t('session-id'),
        field: 'session_id',
        accessorKey: 'session_id',
        minWidth: 140,
        maxWidth: 160,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => {
          const id = getValue() as string;
          const truncated = id.length > 12 ? `${id.slice(0, 12)}...` : id;
          return (
            <div className="flex items-center gap-1">
              <span
                className="font-mono text-xs cursor-pointer hover:underline"
                style={{ color: getColour('normalText') }}
                title={id}
              >
                {truncated}
              </span>
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleCopyId(id);
                }}
                className="p-0.5 rounded-2xl hover:bg-opacity-10"
                style={{ color: getColour('inactiveText') }}
              >
                {copiedId === id ? (
                  <Check className="h-3 w-3 text-green-500 dark:text-green-400" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </div>
          );
        }
      },
      {
        id: 'organization_name',
        header: t('customer'),
        field: 'organization_name',
        accessorKey: 'organization_name',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        id: 'filename',
        header: t('filename'),
        field: 'filename',
        accessorKey: 'filename',
        minWidth: 150,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => {
          const filename = getValue() as string | null;
          if (!filename) return '-';
          const truncated =
            filename.length > 20 ? `${filename.slice(0, 20)}...` : filename;
          return (
            <span
              className="text-xs"
              style={{ color: getColour('normalText') }}
              title={filename}
            >
              {truncated}
            </span>
          );
        }
      },
      {
        id: 'language',
        header: t('language'),
        field: 'language',
        accessorKey: 'language',
        minWidth: 80,
        maxWidth: 100,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <span
            className="text-xs px-1.5 py-0.5 rounded-2xl"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {String(getValue() ?? '')}
          </span>
        )
      },
      {
        id: 'duration',
        header: t('duration'),
        field: 'duration',
        accessorKey: 'duration',
        minWidth: 90,
        maxWidth: 110,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => formatDuration(getValue() as number | null)
      },
      {
        id: 'date_session_start',
        header: t('started'),
        field: 'date_session_start',
        accessorKey: 'date_session_start',
        minWidth: 90,
        maxWidth: 110,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <span style={{ color: getColour('inactiveText') }}>
            {formatRelativeTime(String(getValue() ?? ''))}
          </span>
        )
      }
    ],
    [t, getColour, copiedId]
  );

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view-details',
        label: t('view-details'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => {
          handleCopyId(row.session_id);
        }
      },
      {
        id: 'requeue',
        label: t('requeue'),
        icon: <RotateCcw className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => handleSessionAction(row.session_id, 'requeue'),
        hidden: (row: Row) =>
          !['error', 'stranded', 'uploaded'].includes(row.status)
      },
      {
        id: 'restart',
        label: t('restart'),
        icon: <Play className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => handleSessionAction(row.session_id, 'restart'),
        hidden: (row: Row) =>
          !['error', 'stranded', 'processing'].includes(row.status)
      },
      {
        id: 'kill',
        label: t('kill'),
        icon: <XCircle className="h-4 w-4" />,
        intent: 'danger',
        run: (row: Row) => handleSessionAction(row.session_id, 'kill'),
        hidden: (row: Row) =>
          !['processing', 'queued', 'streaming'].includes(row.status)
      },
      {
        id: 'cleanup',
        label: t('cleanup'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: (row: Row) => handleSessionAction(row.session_id, 'cleanup'),
        hidden: (row: Row) =>
          !['finished', 'error', 'stranded'].includes(row.status)
      }
    ],
    [t, handleCopyId, handleSessionAction]
  );

  return (
    <div className="bg-app min-h-screen h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div
        className="flex-shrink-0 py-2 border-b"
        style={{ borderColor: getColour('border') }}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <PageHeader
            title={t('operations')}
            caption={t('monitor-and-manage-system-operations')}
          />

          <div className="flex items-center gap-3">
            {/* Last updated */}
            <div
              className="flex items-center gap-1.5 text-xs"
              style={{ color: getColour('inactiveText') }}
            >
              <Clock className="h-3.5 w-3.5" />
              <span>{lastUpdated}</span>
            </div>

            {/* Bulk actions - show when sessions are selected */}
            {selectedSessions.length > 0 && (
              <div className="flex items-center gap-2">
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('selected-count', { count: selectedSessions.length })}
                </span>
                <Button
                  label={t('requeue')}
                  variant="secondary"
                  size="small"
                  icon={<RotateCcw className="h-3.5 w-3.5" />}
                  onClick={() => handleBulkAction('requeue')}
                />
                <Button
                  label={t('restart')}
                  variant="secondary"
                  size="small"
                  icon={<Play className="h-3.5 w-3.5" />}
                  onClick={() => handleBulkAction('restart')}
                />
                <Button
                  label={t('kill')}
                  variant="danger"
                  size="small"
                  icon={<XCircle className="h-3.5 w-3.5" />}
                  onClick={() => handleBulkAction('kill')}
                />
              </div>
            )}

            {/* Refresh */}
            <Button
              label={t('refresh')}
              variant="secondary"
              size="small"
              icon={
                <RefreshCw
                  className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                />
              }
              onClick={() => refetch()}
              disabled={isFetching}
            />
          </div>
        </div>

        {/* Status filters */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <StatusChip
            status="all"
            label={t('all')}
            count={sessionCounts?.total ?? totalResults}
            active={statusFilter === 'all'}
          />
          {SESSION_STATUS_OPTIONS.map(status => (
            <StatusChip
              key={status}
              status={status}
              label={t(status)}
              count={getStatusCount(status)}
              active={statusFilter === status}
            />
          ))}
        </div>
      </div>

      {/* Alerts bar - only shows when there are issues */}
      {alerts.length > 0 && (
        <div
          className="flex-shrink-0 px-4 py-2 flex flex-wrap items-center gap-3 border-b"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          {alerts.map(alert => {
            const Icon = alert.icon;
            return (
              <div
                key={alert.key}
                // eslint-disable-next-line no-restricted-syntax
                className={`flex items-center gap-1.5 px-2 py-1 rounded-2xl text-xs font-medium ${alert.bgClass} ${alert.textClass}`}
              >
                <Icon className={`h-3.5 w-3.5 ${alert.iconClass}`} />
                <span>{alert.label}:</span>
                <span className="font-bold">{alert.value}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Error banner */}
      {isError && (
        <div className="flex-shrink-0 px-4 py-2">
          <div
            className="flex items-center gap-2 rounded-2xl bg-red-500/10 border border-red-500/25 px-4 py-3 text-sm"
            style={{ color: 'rgb(220 38 38)' }}
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>{t('failed-to-load-data')}</span>
          </div>
        </div>
      )}

      {/* Table - scrollable */}
      <div className="flex-1 min-h-0 overflow-hidden p-4">
        <div
          className="h-full rounded-2xl overflow-hidden"
          style={{ backgroundColor: getColour('baseLayer') }}
        >
          <DataGridV2<Row>
            data={sessions}
            columns={columns}
            actions={actions}
            enableRowSelection
            onRowSelectionChange={rows =>
              setSelectedSessions(rows.map(r => r.session_id))
            }
            enablePagination
            manualPagination
            enableSorting
            manualSorting={false}
            enableFiltering
            manualFiltering={false}
            totalItems={totalResults}
            pageSize={limit}
            pageSizeOptions={[25, 50, 100]}
            showColumnFilters={false}
            filterMode="popover"
            height="100%"
            dense
            grid={false}
            striped
            loading={isFetching}
            getRowId={row => row.session_id}
          />
        </div>
      </div>

      {/* Single Action Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={() =>
          setConfirmModal({ open: false, sessionId: '', action: null })
        }
        onConfirm={executeSessionAction}
        size="small"
        title={t('confirm-action')}
        caption={t('confirm-session-action-description', {
          action: getActionLabel(confirmModal.action),
          sessionId: confirmModal.sessionId
        })}
        confirmLabel={
          isActionLoading
            ? t('processing')
            : getActionLabel(confirmModal.action)
        }
        cancelLabel={t('cancel')}
        confirmDisabled={isActionLoading}
        destructive={
          confirmModal.action === 'kill' || confirmModal.action === 'cleanup'
        }
        autoFocusConfirm
      />

      {/* Bulk Action Confirmation Modal */}
      <ConfirmationModal
        isOpen={bulkActionModal.open}
        onClose={() => setBulkActionModal({ open: false, action: null })}
        onConfirm={executeBulkAction}
        size="small"
        title={t('confirm-bulk-action')}
        caption={t('confirm-bulk-action-description', {
          action: getActionLabel(bulkActionModal.action),
          count: selectedSessions.length
        })}
        confirmLabel={
          bulkActionMutation.isLoading
            ? t('processing')
            : getActionLabel(bulkActionModal.action)
        }
        cancelLabel={t('cancel')}
        confirmDisabled={bulkActionMutation.isLoading}
        destructive={
          bulkActionModal.action === 'kill' ||
          bulkActionModal.action === 'cleanup'
        }
        autoFocusConfirm
      />
    </div>
  );
}
